import React, { useState, useEffect, useRef } from 'react';
import { Bell, User, Settings, Search as SearchIcon, Menu, LogOut, Check, ShieldCheck, CreditCard, AlertCircle, HelpCircle } from 'lucide-react';
import type { User as UserType, ViewState, Precedent, Notification } from '@/src/types';
// Firebase dynamic imports used inside effects to avoid blocking render

interface TopbarProps {
  user: UserType | null;
  toggleSidebar: () => void;
  onLogout: () => void;
  onNavigate: (view: ViewState) => void;
  bookmarkedIds: Set<string>;
  isAndroid?: boolean;
  currentView?: ViewState;
}

export function Topbar({ user, toggleSidebar, onLogout, onNavigate, bookmarkedIds, isAndroid, currentView }: TopbarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isInitialLoadRef = useRef(true);
  const prevUnreadCountRef = useRef<number>(0);
  const [isRinging, setIsRinging] = useState(false);

  useEffect(() => {
    // Close dropdown on click outside
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!user) return; // Only init if logged in

    let unsubscribe: any;
    const initNotifications = async () => {
      try {
        const { auth, db } = await import('@/src/firebase');
        if (!auth.currentUser) return;

        const { collection, query, onSnapshot } = await import('firebase/firestore');
        const q = query(collection(db, `users/${auth.currentUser.uid}/notifications`));
        unsubscribe = onSnapshot(q, (snapshot) => {
          const notifs: Notification[] = [];
          snapshot.forEach(doc => {
            notifs.push({ id: doc.id, ...doc.data() } as Notification);
          });
          notifs.sort((a, b) => {
             const timeA = a.createdAt?.seconds || 0;
             const timeB = b.createdAt?.seconds || 0;
             return timeB - timeA;
          });
          setNotifications(notifs);
        }, (error) => {
           console.error("Error fetching notifications:", error);
        });
      } catch (err) {
        console.warn("Failed to load Firebase for notifications:", err);
      }
    };

    initNotifications();
    return () => { if (unsubscribe) unsubscribe(); };
  }, [user]);


  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      onNavigate('search');
    }
  };

  const markAsRead = async (id: string) => {
    if (!user) return;
    try {
      const { auth, db } = await import('@/src/firebase');
      if (!auth.currentUser) return;
      const { doc, updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(db, `users/${auth.currentUser.uid}/notifications`, id), {
        read: true
      });
    } catch(e) {
      console.error(e);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    if (isInitialLoadRef.current) {
       isInitialLoadRef.current = false;
       prevUnreadCountRef.current = unreadCount;
       return;
    }
    
    // Play sound if unread count strictly increases
    if (unreadCount > prevUnreadCountRef.current) {
      try {
        const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
        audio.play().catch(e => console.log('Audio auto-play blocked by browser:', e));
        
        // Trigger a visual shake/ring animation
        setIsRinging(true);
        setTimeout(() => setIsRinging(false), 2000);
      } catch (e) {
        console.error("Audio error", e);
      }
    }
    
    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount]);

  const getNotifIcon = (type?: string) => {
    switch (type) {
      case 'verification': return <ShieldCheck className="w-4 h-4 text-emerald-600" />;
      case 'subscription': return <CreditCard className="w-4 h-4 text-indigo-600" />;
      case 'precedent': return <SearchIcon className="w-4 h-4 text-blue-600" />;
      default: return <AlertCircle className="w-4 h-4 text-slate-600" />;
    }
  };

  const getNotifBg = (type?: string, read?: boolean) => {
    if (read) return 'bg-white';
    switch (type) {
      case 'verification': return 'bg-emerald-50/50';
      case 'subscription': return 'bg-indigo-50/50';
      case 'precedent': return 'bg-blue-50/50';
      default: return 'bg-slate-50/50';
    }
  };

  // Helper to format title for Android App Bar
  const getAppTitle = () => {
    switch(currentView) {
      case 'dashboard': return 'Dashboard';
      case 'search': return 'Search Precedents';
      case 'workspace': return 'Workspace';
      case 'advocate-dashboard': return 'Lawyer Dashboard';
      case 'advocates': return 'Legal Directory';
      case 'client-portal': return 'Client Portal';
      case 'my-cases': return 'My Cases';
      case 'repository': return 'Case Repository';
      default: return 'PakLawSite';
    }
  };

  return (
    <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 shrink-0 z-10 sticky top-0 shadow-sm">
      <div className="flex items-center gap-4 flex-1">
        <button 
          onClick={toggleSidebar}
          className="p-2 hover:bg-slate-100 rounded-md text-slate-600 lg:hidden"
          aria-label="Toggle navigation menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Mobile Brand Logo */}
        <button 
          onClick={() => onNavigate('landing')} 
          className="flex lg:hidden items-center gap-2 text-left focus:outline-none"
        >
          <div className="w-7 h-7 rounded-lg bg-slate-900 border border-slate-700 p-0.5 flex items-center justify-center shrink-0 shadow-xs">
            <img src="/logo.svg" alt="PakLawSite Logo" className="w-full h-full object-contain" />
          </div>
          <span className="font-sans text-sm font-black text-slate-900 tracking-tight">
            PakLegal <span className="text-emerald-700 font-bold">AI</span>
          </span>
        </button>
        
        <div className="hidden md:flex relative max-w-md w-full">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input 
            type="text" 
            placeholder="Quick search (press Enter to go to search view)..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={handleSearch}
            className="w-full pl-9 pr-4 py-2 bg-slate-100 border-transparent rounded-md text-sm focus:bg-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all outline-none"
          />
        </div>
      </div>

      <div className="flex items-center gap-3 sm:gap-6 relative">
        <div ref={dropdownRef} className="relative">
          <button 
             onClick={() => setShowDropdown(!showDropdown)}
             className="relative p-2 text-slate-500 hover:bg-slate-100 rounded-full transition-colors focus:outline-none"
             aria-label="Notifications"
          >
            <Bell className={`w-5 h-5 ${isRinging ? 'animate-bounce text-emerald-600' : ''}`} />
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white">
                {unreadCount}
              </span>
            )}
          </button>
          
          <button 
             onClick={() => window.dispatchEvent(new CustomEvent('restart-paklegal-tutorial'))}
             className="hidden sm:flex p-2 text-slate-500 hover:bg-slate-100 rounded-full transition-colors mr-1 group relative"
             title="Restart Tutorial"
             aria-label="Restart Tutorial"
          >
            <HelpCircle className="w-5 h-5" />
            <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">Tutorial</span>
          </button>
          
          {showDropdown && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-slate-200 py-0 z-50 overflow-hidden transform origin-top-right transition-all">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                <span className="font-bold text-slate-800 text-sm">Notifications</span>
                {unreadCount > 0 && <span className="text-[10px] font-black bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded-full uppercase tracking-tighter">{unreadCount} New</span>}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-4 py-12 text-center">
                    <Bell className="w-8 h-8 text-slate-200 mx-auto mb-2 opacity-20" />
                    <p className="text-xs text-slate-500 italic">No notifications yet.</p>
                  </div>
                ) : (
                  notifications.map(notif => (
                    <div key={notif.id} className={`px-4 py-4 border-b border-slate-50 relative group transition-colors ${getNotifBg(notif.type, notif.read)}`}>
                      <div className="flex gap-3">
                        <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center ${notif.read ? 'bg-slate-100' : 'bg-white shadow-sm'}`}>
                           {getNotifIcon(notif.type)}
                        </div>
                        <div className="pr-6">
                          <p className={`text-xs font-bold mb-0.5 ${notif.read ? 'text-slate-600' : 'text-slate-900'}`}>{notif.title}</p>
                          <p className="text-[11px] text-slate-500 leading-normal">{notif.message}</p>
                          <p className="text-[9px] text-slate-500 mt-1 font-medium">{notif.createdAt?.seconds ? new Date(notif.createdAt.seconds * 1000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'Just now'}</p>
                        </div>
                      </div>
                      {!notif.read && (
                        <button 
                          onClick={() => markAsRead(notif.id)} 
                          className="absolute right-2 top-4 p-1.5 hover:bg-white hover:shadow-sm rounded-full transition-all group/btn"
                          title="Mark as read"
                          aria-label="Mark notification as read"
                        >
                           <Check className="w-3.5 h-3.5 text-slate-300 group-hover/btn:text-emerald-600 transition-colors" />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
              {notifications.length > 0 && (
                <div className="p-2 bg-slate-50 border-t border-slate-100 flex justify-center">
                   <button onClick={() => setShowDropdown(false)} className="text-[10px] font-bold text-slate-500 hover:text-slate-600 uppercase tracking-widest">Close Center</button>
                </div>
              )}
            </div>
          )}
        </div>
        
        {user ? (
          <div className="flex items-center gap-3 pl-2 sm:pl-6 border-l border-slate-200">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-semibold text-slate-700 leading-none">{user.name}</p>
              <p className="text-xs text-slate-500 mt-1">{user.role}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold border border-emerald-200">
              {user.name.charAt(0)}
            </div>
            <button 
              onClick={onLogout}
              className="p-2 ml-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors border border-transparent" 
              title="Log out"
              aria-label="Log out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 pl-2 sm:pl-6 border-l border-slate-200">
            <button 
              onClick={() => onNavigate('login')}
              className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold px-4 py-2 rounded-lg shadow-sm transition-colors cursor-pointer"
            >
              Sign In / Register
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
